import {  t  } from "@/client/libs/dummy-i18n";
import { ArrowUUpLeftIcon, ClockIcon, ShieldCheckIcon, StarIcon } from "@phosphor-icons/react";
import { Button, Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@stackmorph/ui";
import axios from "axios";
import { useEffect, useState } from "react";

export const PricingModal = ({ isOpen, setIsOpen }: { isOpen: boolean; setIsOpen: (open: boolean) => void }) => {
  const [country, setCountry] = useState<string | null>(null);

  useEffect(() => {
    // Simple country detection using timezone or browser locale as a proxy
    // In a real app, use an IP geolocation service
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (timezone.includes("Calcutta") || timezone.includes("Kolkata") || timezone.includes("India")) {
      setCountry("IN");
    } else {
      setCountry("US"); // Default to US/Global
    }
  }, []);

  const handleUpgrade = async () => {
    try {
      const provider = country === "IN" ? "razorpay" : "paypal";
      const currency = country === "IN" ? "INR" : "USD";
      const amount = country === "IN" ? 499 : 10; // Example pricing

      const { data: order } = await axios.post("/payment/create-order", {
        amount,
        currency,
        provider,
      });

      if (provider === "razorpay") {
        const options = {
          key: process.env.RAZORPAY_KEY_ID, // Should be public key
          amount: order.amount,
          currency: order.currency,
          name: t`StackMorph Resume Builder`,
          description: t`Premium Upgrade`,
          order_id: order.id,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          handler: async function (response: any) {
            await axios.post("/payment/verify-razorpay", {
              orderId: response.razorpay_order_id,
              paymentId: response.razorpay_payment_id,
              signature: response.razorpay_signature,
              itemId: "premium",
            });
            setIsOpen(false);
            window.location.reload(); // Reload to reflect status
          },
        };
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const rzp = new (window as any).Razorpay(options);
        rzp.open();
      } else {
        // PayPal flow
        alert(t`PayPal integration coming soon!`);
      }
    } catch (error) {
      console.error("Payment failed", error);
      alert(t`Payment failed. Please try again.`);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t`Upgrade to Premium`}</DialogTitle>
          <DialogDescription>
            {t`Unlock unlimited resumes and exclusive features.`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-lg border p-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold">{t`Premium Plan`}</h3>
              <div className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/30 dark:text-amber-200">
                <ClockIcon className="mr-1 h-3 w-3" />
                {t`Limited Time Offer`}
              </div>
            </div>
            
            <p className="text-2xl font-bold mt-2">
              {country === "IN" ? "₹499" : "$10"} <span className="text-sm font-normal text-muted-foreground">/ {t`lifetime`}</span>
            </p>
            <ul className="mt-4 space-y-2 text-sm">
              <li>
                <span role="img" aria-label="check">✅</span> {t`Unlimited Resumes`}
              </li>
              <li>
                <span role="img" aria-label="check">✅</span> {t`Remove Branding`}
              </li>
              <li>
                <span role="img" aria-label="check">✅</span> {t`Priority Support`}
              </li>
              <li>
                <span role="img" aria-label="check">✅</span> {t`AI Writing Assistant`}
              </li>
              <li>
                <span role="img" aria-label="check">✅</span> {t`Version History`}
              </li>
            </ul>
            <Button className="w-full mt-4" onClick={handleUpgrade}>
              {t`Upgrade Now`}
            </Button>

            <div className="mt-4 flex justify-center gap-4 text-xs text-muted-foreground">
              <div className="flex items-center gap-1">
                <ShieldCheckIcon className="h-4 w-4" />
                <span>{t`Secure Payment`}</span>
              </div>
              <div className="flex items-center gap-1">
                <ArrowUUpLeftIcon className="h-4 w-4" />
                <span>{t`Cancel Anytime`}</span>
              </div>
            </div>
          </div>

          <div className="rounded-lg bg-muted/50 p-4">
            <div className="mb-3 flex items-center gap-2">
              <div className="flex -space-x-2">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-6 w-6 rounded-full border-2 border-background bg-gradient-to-br from-gray-300 to-gray-400" />
                ))}
              </div>
              <div className="flex items-center gap-1">
                <div className="flex text-amber-500">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <StarIcon key={i} size={12} weight="fill" />
                  ))}
                </div>
                <span className="text-xs font-medium">{t`Loved by 10,000+ users`}</span>
              </div>
            </div>
            <p className="text-sm italic text-muted-foreground">
              "{t`This builder helped me land my dream job at a top tech company. The AI features are incredible!`}"
            </p>
            <p className="mt-1 text-xs font-bold">- Sarah J., Software Engineer</p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
